import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import WhatsAppIcon from "./WhatsAppIcon";
import { useSite } from "@/lib/site";
import { waLink } from "@/lib/api";

const LINKS = {
  ar: [
    { href: "#about", label: "من نحن" },
    { href: "#features", label: "المميزات" },
    { href: "#gallery", label: "التطبيق" },
    { href: "#owners", label: "لأصحاب الصالونات" },
    { href: "#faq", label: "الأسئلة" },
    { href: "#contact", label: "تواصل معنا" },
  ],
  fr: [
    { href: "#about", label: "À propos" },
    { href: "#features", label: "Fonctionnalités" },
    { href: "#gallery", label: "Application" },
    { href: "#owners", label: "Salons" },
    { href: "#faq", label: "FAQ" },
    { href: "#contact", label: "Contact" },
  ],
};

export default function Navbar() {
  const { tc, lang, setLang } = useSite();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const wa = waLink(tc?.contact?.whatsappNumber, tc?.contact?.whatsappMessage);
  const links = LINKS[lang] || LINKS.ar;

  return (
    <div
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled || open ? "bg-forest-950/95 shadow-lg shadow-black/20 backdrop-blur-md" : "bg-transparent"
      }`}
    >
      <nav className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-5 py-3">
        <a href="#top" className="flex items-center gap-3" data-testid="navbar-logo-link">
          <img src="/logo.jpg" alt="شعار حلاق - 7ala9" className="h-11 w-auto rounded-xl shadow-md" />
        </a>
        <ul className="hidden items-center gap-7 lg:flex">
          {links.map((l) => (
            <li key={l.href}>
              <a
                href={l.href}
                data-testid={`navbar-link-${l.href.replace("#", "")}`}
                className="text-sm font-medium text-cream/85 transition hover:text-gold"
              >
                {l.label}
              </a>
            </li>
          ))}
        </ul>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setLang(lang === "ar" ? "fr" : "ar")}
            data-testid="navbar-lang-toggle"
            aria-label="Changer de langue / تغيير اللغة"
            className="rounded-full border border-cream/25 px-3 py-1.5 text-xs font-bold text-cream transition hover:border-gold hover:text-gold"
          >
            {lang === "ar" ? "FR" : "عربي"}
          </button>
          <a
            href={wa}
            target="_blank"
            rel="noreferrer"
            data-testid="navbar-whatsapp-button"
            className="hidden items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-forest-950 transition hover:bg-gold-light sm:flex"
          >
            <WhatsAppIcon className="h-4 w-4" />
            {lang === "fr" ? "WhatsApp" : "واتساب"}
          </a>
          <button
            onClick={() => setOpen(!open)}
            data-testid="navbar-mobile-toggle"
            aria-label="القائمة"
            className="rounded-xl border border-gold/30 p-2 text-cream lg:hidden"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>
      {open && (
        <div className="border-t border-gold/15 bg-forest-950/95 px-5 pb-6 pt-3 backdrop-blur-md lg:hidden">
          <ul className="space-y-1">
            {links.map((l) => (
              <li key={l.href}>
                <a
                  href={l.href}
                  onClick={() => setOpen(false)}
                  data-testid={`navbar-mobile-link-${l.href.replace("#", "")}`}
                  className="block rounded-lg px-3 py-2.5 text-sm font-medium text-cream/90 transition hover:bg-forest-800 hover:text-gold"
                >
                  {l.label}
                </a>
              </li>
            ))}
          </ul>
          <a
            href={wa}
            target="_blank"
            rel="noreferrer"
            data-testid="navbar-mobile-whatsapp-button"
            className="mt-4 flex items-center justify-center gap-2 rounded-full bg-gold px-5 py-3 text-sm font-bold text-forest-950"
          >
            <WhatsAppIcon className="h-4 w-4" />
            {lang === "fr" ? "Contacter via WhatsApp" : "تواصل عبر واتساب"}
          </a>
        </div>
      )}
    </div>
  );
}
